// src/categories/CategoryPage.js
import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Link, NavLink } from 'react-router-dom';
import { FaCheckCircle, FaFileAlt } from 'react-icons/fa';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { AuthContext } from '../context/AuthContext';
import {
  fetchTitles, fetchCommentsForTitle, fetchDocumentsForTitle, fetchUserCategory, updateUserCategory,
} from '../services/apiService';
import {
  handleCommentChange, handleCommentSubmit, handleDownload,
  handleDeleteComment, handleDocumentUpload, handleDocumentDelete,
  handleChangeDocumentStatus,
} from '../utils/actions';
import TitleList from '../components/TitleList';
import titlesByCategory from './titlesCategories';
import categoryIdMapping from './categoryIdMapping';
import menuItemsByService from './serviceMenus';
import 'react-circular-progressbar/dist/styles.css';
import { useSidebar } from '../context/SidebarContext';

const CERTIFICATE_CATEGORIES = [
  'anexo30Y31Ano2024Certificado',
  'anexo30Y31Ano2025Certificado',
  'anexo21Y22Rmf2026Certificado',
  'transporteAnexo21Y22Rmf2026Certificado2026',
  'distribucionAnexo21Y22Rmf2026Certificado2026',
  'comercializadoraAnexo21Y22Rmf2026Certificado2026',
];

// Función auxiliar para obtener el nombre del servicio basado en el ID de la categoría
const getServiceNameFromCategory = (categoryId) => {
  if (categoryId.includes('anexo30Y31Ano2022')) {
    return 'Anexo 30 y 31 2022';
  }
  if (categoryId.includes('anexo30Y31Ano2023')) {
    return 'Anexo 30 y 31 2023';
  }
  if (categoryId.includes('anexo30Y31Ano2024')) {
    return 'Anexo 30 y 31 2024';
  }
  if (categoryId.includes('anexo30Y31Ano2025')) {
    return 'Anexo 30 y 31 2025';
  }
  if (categoryId.includes('dictamenDeDiseno')) {
    return 'Dictamen de Diseño';
  }
  if (categoryId.includes('nom016')) {
    return 'NOM-016-CRE-2016';
  }
  if (categoryId.includes('inspeccion30Y31Ano2024')) {
    return 'Anexo 30 y 31 para transporte o distribución';
  }
  if (categoryId.includes('distribucionAnexo21Y22Rmf2026')) {
    return 'Distribución Anexo 21 y 22 rmf 2026';
  }
  if (categoryId.includes('comercializadoraAnexo21Y22Rmf2026')) {
    return 'Comercializadora Anexo 21 y 22 rmf 2026';
  }
  // Antes que anexo21Y22Rmf2026: esas cadenas son subcadena de transporteAnexo21Y22Rmf2026…
  if (categoryId.includes('transporteAnexo21Y22Rmf2026')) {
    return 'Transporte Anexo 21 y 22 rmf 2026';
  }
  if (categoryId.includes('anexo21Y22Rmf2026')) {
    return 'Anexo 21 y 22 rmf 2026';
  }
  return null;
};

function CategoryPage({ category, pageTitle }) {
  const [titles, setTitles] = useState([]);
  const [newComments, setNewComments] = useState({});
  const [documentIndicator, setDocumentIndicator] = useState('');
  const [requirementsIndicator, setRequirementsIndicator] = useState('');
  const { isSidebarOpen } = useSidebar();
  const { authTokens, impersonatedUserId } = useContext(AuthContext);

  // Get the service name for the current category
  const serviceName = getServiceNameFromCategory(category);
  // Filtramos el botón de "Inicio" del final del menú
  const menuItems = serviceName ? menuItemsByService[serviceName].filter((item) => item.name !== 'Inicio') : [];

  const fetchUserCategoryData = useCallback(async () => {
    if (!categoryIdMapping[category]) return;

    const categoryId = categoryIdMapping[category];
    const userCategory = await fetchUserCategory(authTokens, impersonatedUserId || authTokens.userId, categoryId);

    if (userCategory) {
      setDocumentIndicator(`${userCategory.documents_uploaded} de ${userCategory.documents_total} documentos subidos`);
      setRequirementsIndicator(`${userCategory.documents_completed} de ${userCategory.documents_total} documentos completados`);
    }
  }, [authTokens, impersonatedUserId, category]);

  const synchronizeWithDb = useCallback(async (documentsUploaded, documentsCompleted, totalDocuments) => {
    if (!categoryIdMapping[category]) return;

    const categoryId = categoryIdMapping[category];
    const userId = impersonatedUserId || authTokens.userId;

    const data = {};
    if (documentsUploaded !== null) {
      data.documents_uploaded = documentsUploaded;
    }
    if (documentsCompleted !== null) {
      data.documents_completed = documentsCompleted;
    }
    if (totalDocuments !== null) {
      data.documents_total = totalDocuments;
    }
    await updateUserCategory(authTokens, userId, categoryId, data, impersonatedUserId);
    fetchUserCategoryData();
  }, [authTokens, impersonatedUserId, category, fetchUserCategoryData]);

  const updateDocumentIndicator = useCallback((updatedTitles) => {
    const totalTitles = titlesByCategory[category].length;
    const titlesWithAtLeastOneDocument = updatedTitles.filter((title) => title.documents.length > 0).length;
    setDocumentIndicator(`${titlesWithAtLeastOneDocument} de ${totalTitles} documentos subidos`);
    synchronizeWithDb(titlesWithAtLeastOneDocument, null, totalTitles);
  }, [category, synchronizeWithDb]);

  const updateRequirementsIndicator = useCallback((updatedTitles) => {
    const totalTitles = titlesByCategory[category].length;
    const titlesWithAllDocumentsComplete = updatedTitles.filter(
      (title) => title.documents.length > 0 && title.documents.every((document) => document.status === 'complete'),
    ).length;
    setRequirementsIndicator(`${titlesWithAllDocumentsComplete} de ${totalTitles} documentos completos`);
    synchronizeWithDb(null, titlesWithAllDocumentsComplete, totalTitles);
  }, [category, synchronizeWithDb]);

  useEffect(() => {
    const isAdminAndImpersonating = (
      authTokens?.userRole === 'admin' || authTokens?.userRole === 'super_admin') && impersonatedUserId;
    const shouldLoadData = authTokens?.userRole === 'user' || isAdminAndImpersonating;

    if (shouldLoadData) {
      const loadTitlesCommentsAndDocuments = async () => {
        const fetchedTitles = await fetchTitles(authTokens, impersonatedUserId || authTokens.userId, category);
        const titlesWithCommentsAndDocumentsPromises = fetchedTitles.map(async (title) => {
          const [comments, documents] = await Promise.all([
            fetchCommentsForTitle(title.id, authTokens, impersonatedUserId || authTokens.userId),
            fetchDocumentsForTitle(title.id, authTokens, impersonatedUserId || authTokens.userId),
          ]);
          return { ...title, comments, documents };
        });
        const titlesWithCommentsAndDocuments = await Promise.all(titlesWithCommentsAndDocumentsPromises);
        const filteredTitles = titlesWithCommentsAndDocuments.filter((title) => titlesByCategory[category].includes(title.id));

        setTitles(filteredTitles);
        updateDocumentIndicator(filteredTitles);
        updateRequirementsIndicator(filteredTitles);
        fetchUserCategoryData();
      };

      loadTitlesCommentsAndDocuments();
    } else if ((authTokens?.userRole === 'admin' || authTokens?.userRole === 'super_admin') && !impersonatedUserId) {
      setTitles([]);
    }
  }, [authTokens, impersonatedUserId, category, updateDocumentIndicator, updateRequirementsIndicator, fetchUserCategoryData]);

  const getProgressColor = (uploaded, total) => {
    if (total === 0) return 'gray'; // Gris para casos de 0 de 0
    const percentage = (uploaded / total) * 100;
    if (percentage < 25) return 'red';
    if (percentage < 50) return 'orange';
    if (percentage < 99) return 'yellow';
    return 'green';
  };

  const calculatePercentage = (uploaded, total) => (total === 0 ? 0 : (uploaded / total) * 100);

  if ((authTokens?.userRole === 'admin' || authTokens?.userRole === 'super_admin') && !impersonatedUserId) {
    return <div>Por favor, seleccione un usuario para acceder a la información.</div>;
  }

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <div
        className={`fixed md:fixed top-16 left-0 h-[calc(100vh-4rem)] overflow-y-auto transform
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          transition duration-200 ease-in-out
          w-64 bg-white shadow-lg z-40`}
      >
        <div className="p-8 border-b">
          <img src="/images/logo2.png" alt="Logo Petrotec" className="h-32 w-auto mx-auto" />
        </div>
        <nav className="mt-6">
          <Link
            to="/dashboard"
            className={`
              block px-4 py-3 hover:bg-blue-50 transition-colors duration-200
              text-gray-700 hover:text-blue-600 border-b border-gray-100
            `}
          >
            Inicio
          </Link>
          <div className="px-4 py-2">
            <h2 className="text-xs uppercase font-semibold text-gray-600 tracking-wide">
              Menú de Navegación
            </h2>
          </div>
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `
                block px-4 py-3 transition-colors duration-200
                ${isActive ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}
              `}
            >
              {item.name}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-200 ${isSidebarOpen ? 'md:ml-64' : ''}`}>

        <div className="p-8 pb-16">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
            <p className="mt-2 text-gray-600">Gestiona tus documentos y requisitos.</p>
          </div>

          {/* Progress Indicators */}
          {!CERTIFICATE_CATEGORIES.includes(category) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div
                className={`
                  bg-white p-6 rounded-lg shadow-sm transition-all duration-300
                  hover:shadow-xl hover:-translate-y-1 border border-gray-100
                `}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-24 h-24">
                    <CircularProgressbar
                      value={calculatePercentage(
                        parseInt(documentIndicator.split(' de ')[0], 10),
                        parseInt(documentIndicator.split(' de ')[1], 10),
                      )}
                      styles={buildStyles({
                        pathColor: getProgressColor(
                          parseInt(documentIndicator.split(' de ')[0], 10),
                          parseInt(documentIndicator.split(' de ')[1], 10),
                        ),
                        trailColor: '#f3f4f6',
                        strokeLinecap: 'round',
                      })}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center text-gray-700 mb-1">
                      <FaFileAlt className="text-blue-500 w-4 h-4 mr-2" />
                      <h2 className="text-base font-medium">Documentos Subidos</h2>
                    </div>
                    <p className="text-2xl font-semibold text-gray-900">{documentIndicator}</p>
                  </div>
                </div>
              </div>

              <div
                className={`
                  bg-white p-6 rounded-lg shadow-sm transition-all duration-300
                  hover:shadow-xl hover:-translate-y-1 border border-gray-100
                `}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-24 h-24">
                    <CircularProgressbar
                      value={calculatePercentage(
                        parseInt(requirementsIndicator.split(' de ')[0], 10),
                        parseInt(requirementsIndicator.split(' de ')[1], 10),
                      )}
                      styles={buildStyles({
                        pathColor: getProgressColor(
                          parseInt(requirementsIndicator.split(' de ')[0], 10),
                          parseInt(requirementsIndicator.split(' de ')[1], 10),
                        ),
                        trailColor: '#f3f4f6',
                        strokeLinecap: 'round',
                      })}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center text-gray-700 mb-1">
                      <FaCheckCircle className="text-green-500 w-4 h-4 mr-2" />
                      <h2 className="text-base font-medium">Documentos Verificados</h2>
                    </div>
                    <p className="text-2xl font-semibold text-gray-900">{requirementsIndicator}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Title List */}
          <div className="bg-white rounded-lg shadow-md">
            <TitleList
              titles={titles}
              onDownload={handleDownload}
              onDocumentUpload={(e, titleId) => handleDocumentUpload(
                e,
                titleId,
                authTokens,
                setTitles,
                titles,
                impersonatedUserId,
                updateDocumentIndicator,
                updateRequirementsIndicator,
                fetchUserCategoryData,
              )}
              onDocumentDelete={(titleId, documentId) => handleDocumentDelete(
                titleId,
                documentId,
                authTokens,
                setTitles,
                titles,
                impersonatedUserId,
                updateDocumentIndicator,
                updateRequirementsIndicator,
                fetchUserCategoryData,
              )}
              handleChangeDocumentStatus={(titleId, documentId, newStatus) => handleChangeDocumentStatus(
                titleId,
                documentId,
                newStatus,
                authTokens,
                setTitles,
                titles,
                impersonatedUserId,
                updateDocumentIndicator,
                updateRequirementsIndicator,
                fetchUserCategoryData,
              )}
              onCommentSubmit={(e, titleId) => handleCommentSubmit(
                e,
                titleId,
                newComments,
                authTokens,
                setNewComments,
                setTitles,
                titles,
                impersonatedUserId,
              )}
              onCommentChange={(e, titleId) => handleCommentChange(e, titleId, setNewComments, newComments)}
              onCommentDelete={(titleId, commentId) => handleDeleteComment(
                titleId,
                commentId,
                authTokens,
                setTitles,
                titles,
                impersonatedUserId,
              )}
              newComments={newComments}
              authTokens={authTokens}
              userRole={authTokens?.userRole}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

CategoryPage.propTypes = {
  category: PropTypes.string.isRequired,
  pageTitle: PropTypes.string.isRequired,
};

export default CategoryPage;
